import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaEyeSlash, FaEye, FaEnvelope, FaCheckCircle/*, FaExclamationCircle*/ } from "react-icons/fa";
import toast from "react-hot-toast";
import statesData from "../nigeriaStates.json";
import { signUp, getBanks, registerEmail } from "../services/onboarding";
// import { verifyAccount } from "../services/settings";
import { ReactComponent as Spinner } from "../assets/icons/spinner.svg";
import PayloftLogo from "../assets/images/payloft-logo.png";
import "../App.css";
import { getBusinessTypes } from "../services/merchant";

const SignUp = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Stage 1: Credentials
  const [emailAddress, setEmailAddress] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [inputPassNew, setInputPassNew] = useState("password");
  const [showNew, setShowNew] = useState(false);
  const [inputPassConfirm, setInputPassConfirm] = useState("password");
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  // Stage 3: Business & Personal Details
  const [businessName, setBusinessName] = useState("");
  const [businessEmail, setBusinessEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [address, setAddress] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [cities, setCities] = useState([]);
  const [businessType, setBusinessType] = useState("");
  const [businessTypes, setBusinessTypes] = useState([]);
  const [cac, setCac] = useState("");

  // Stage 4: Bank Details & Verification
  const [banks, setBanks] = useState([]);
  const [bank, setBank] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountName, setAccountName] = useState("");
  // const [verifyingAccount, setVerifyingAccount] = useState(false);

  // Stage 5: Terms & Conditions
  const [agreed, setAgreed] = useState(false);



  // Parse Step & Email from URL on Mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stepParam = urlParams.get("step");
    const emailParam = urlParams.get("email");

    if (stepParam === "profile") {
      setCurrentStep(3);
      if (emailParam) {
        try {
          const decoded = atob(emailParam);
          setEmailAddress(decoded);
          setBusinessEmail(decoded);
        } catch (e) {
          setEmailAddress(emailParam);
          setBusinessEmail(emailParam);
        }
      }
    }
  }, []);

  // Fetch initial dropdown items (Banks and Business Types)
  const fetchBanks = async () => {
    try {
      const response = await getBanks();
      const sortedBanks = (response?.data || []).sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setBanks(sortedBanks);
    } catch (error) {
      console.error("Error fetching banks:", error);
    }
  };

  const fetchBusinessType = async () => {
    try {
      const res = await getBusinessTypes();
      setBusinessTypes(res.data || []);
    } catch (error) {
      console.error("Error fetching business types:", error);
    }
  };

  useEffect(() => {
    fetchBanks();
    fetchBusinessType();
  }, []);

  // Sync cities dropdown based on state selection
  useEffect(() => {
    if (selectedState && statesData.states[selectedState]) {
      setCities(statesData.states[selectedState]);
    } else {
      setCities([]);
    }
  }, [selectedState]);

  // Bank Verification Trigger logic (commented out for now, to be added back later)
  /*
  useEffect(() => {
    const runVerify = async () => {
      if (accountNumber.length === 10 && bank) {
        setVerifyingAccount(true);
        setAccountName("");
        try {
          const res = await verifyAccount({
            bankCode: bank,
            accountNumber: accountNumber,
          });
          if (res.data?.beneficiaryName) {
            setAccountName(res.data.beneficiaryName);
          } else {
            setAccountName("Account name not found");
          }
        } catch (error) {
          setAccountName("Account name not found");
        } finally {
          setVerifyingAccount(false);
        }
      } else {
        setAccountName("");
      }
    };
    runVerify();
  }, [accountNumber, bank]);
  */

  const handleShowNew = (e) => {
    e.preventDefault();
    setShowNew(!showNew);
    setInputPassNew(inputPassNew === "password" ? "text" : "password");
  };

  const handleShowConfirm = (e) => {
    e.preventDefault();
    setShowConfirm(!showConfirm);
    setInputPassConfirm(inputPassConfirm === "password" ? "text" : "password");
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 11) {
      setPhoneNumber(value);
    }
  };

  const handleAccountNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 10) {
      setAccountNumber(value);
    }
  };

  const validatePassword = (pwd) => {
    const hasNumber = /\d/;
    const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/;

    if (pwd.length < 9 || !hasNumber.test(pwd) || !hasSpecialChar.test(pwd)) {
      throw new Error(
        "Password must be at least 9 characters long and contain a number and a special character."
      );
    }
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    try {
      validatePassword(newPassword);
      setPasswordError("");
    } catch (error) {
      setPasswordError(error.message);
    }
  };

  // Step 1: Submit Credentials
  const handleRegisterCredentials = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    try {
      validatePassword(password);
    } catch (err) {
      toast.error(err.message);
      return;
    }

    setLoading(true);
    try {
      await registerEmail({
        emailAddress: emailAddress,
        password: password,
      });
      toast.success("Verification link sent to your email!");
      setCurrentStep(2);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message || "Failed to register credentials");
      } else {
        toast.error("An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Resend Email option in Step 2
  const handleResendVerification = async () => {
    setLoading(true);
    try {
      await registerEmail({
        emailAddress: emailAddress,
        password: password,
      });
      toast.success("Verification link resent successfully!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to resend verification email");
    } finally {
      setLoading(false);
    }
  };

  // Step 3 Validation & Next
  const handleNextToBank = (e) => {
    e.preventDefault();
    if (!businessName || !businessEmail || !firstName || !lastName || !phoneNumber || !address || !selectedState || !selectedCity || !businessType || !cac) {
      toast.error("Please fill in all profile fields");
      return;
    }
    setCurrentStep(5);
  };

  // Business Name vs Account Name comparison algorithm (Word overlapping) (commented out for now)
  /*
  const validateBusinessAndAccountName = (biz, acct) => {
    if (!biz || !acct) return false;

    const cleanBiz = biz.toLowerCase().replace(/[^a-z0-9\s]/g, "").split(/\s+/).filter(Boolean);
    const cleanAcct = acct.toLowerCase().replace(/[^a-z0-9\s]/g, "").split(/\s+/).filter(Boolean);

    const ignoreWords = [
      "ltd", "limited", "enterprise", "ent", "ventures", "venture", "plc",
      "corp", "co", "company", "and", "global", "integrated", "services"
    ];

    const bizWords = cleanBiz.filter(w => !ignoreWords.includes(w));
    const acctWords = cleanAcct.filter(w => !ignoreWords.includes(w));

    const wordsToCompareBiz = bizWords.length > 0 ? bizWords : cleanBiz;
    const wordsToCompareAcct = acctWords.length > 0 ? acctWords : cleanAcct;

    return wordsToCompareBiz.some(word => wordsToCompareAcct.includes(word));
  };
  */

  // Step 4 Validate Account & Next
  const handleNextToTerms = (e) => {
    e.preventDefault();
    if (!bank || !accountNumber) {
      toast.error("Please select your bank and enter account number");
      return;
    }

    /*
    if (!accountName || accountName === "Account name not found") {
      toast.error("Please provide a valid, verifiable bank account");
      return;
    }

    // Run custom comparison check
    const isMatched = validateBusinessAndAccountName(businessName, accountName);
    if (!isMatched) {
      toast.error(`Validation Mismatch: The registered business name "${businessName}" does not match the bank account beneficiary name "${accountName}".`);
      return;
    }

    toast.success("Validation Success: Business name matches bank account name!");
    */
    setCurrentStep(5);
  };

  // Step 5: Final Submission to signUp (/account/register)
  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      toast.error("You must agree to the terms to complete registration.");
      return;
    }

    setLoading(true);
    try {
      const response = await signUp({
        BusinessName: businessName,
        PhoneNumber: phoneNumber,
        EmailAddress: emailAddress,
        Password: password,
        Title: "Mr",
        FirstName: firstName,
        LastName: lastName,
        BusinessEmail: businessEmail,
        Address: address,
        City: selectedCity,
        State: selectedState,
        Bank: bank,
        AccountName: accountName,
        AccountNumber: accountNumber,
        businessType: businessType,
        cac: cac,
      });
      toast.success(response.message || "Registration Successful!");
      setCurrentStep(6);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message || "Sign up failed");
      } else {
        toast.error("An error occurred during registration. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const getProgressWidth = () => {
    if (currentStep === 1) return 0;
    if (currentStep === 2) return 33;
    if (currentStep === 3) return 66;
    if (currentStep >= 5) return 100;
    return 0;
  };

  return (
    <div className="bg-arena-background min-h-screen py-12 px-4 flex flex-col justify-center items-center">
      <div className="bg-white w-full max-w-[500px] p-8 rounded-xl shadow-md">
        {/* Logo */}
        <div className="flex items-center justify-center mb-6">
          <img
            src={window.__TENANT__?.logo ?? PayloftLogo}
            alt="logo"
            className="w-[100px] h-auto"
          />
        </div>

        {/* Stepper Header */}
        {currentStep <= 5 && (
          <div className="mb-8">
            <div className="flex justify-between items-center relative">
              <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[2px] bg-slate-100 z-0"></div>
              <div
                className="absolute left-0 top-1/2 -translate-y-1/2 h-[2px] bg-primary transition-all duration-300 z-0"
                style={{ width: `${getProgressWidth()}%` }}
              ></div>

              {[
                { label: "Credentials", step: 1 },
                { label: "Verify", step: 2 },
                { label: "Profile", step: 3 },
                { label: "Terms", step: 5 },
              ].map((item) => (
                <div key={item.step} className="flex flex-col items-center z-10 relative">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs border-2 transition-all duration-300 ${
                      currentStep >= item.step
                        ? "bg-primary border-primary text-white"
                        : "bg-white border-slate-200 text-slate-400"
                    }`}
                  >
                    {currentStep > item.step ? "✓" : item.step === 5 ? 4 : item.step}
                  </div>
                  <span
                    className={`text-[9px] mt-1 font-semibold transition-all duration-300 ${
                      currentStep >= item.step ? "text-primary" : "text-slate-400"
                    }`}
                  >
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* STEP 1: Account Credentials */}
        {currentStep === 1 && (
          <form onSubmit={handleRegisterCredentials} className="space-y-4">
            <h2 className="text-xl font-bold text-navy-blue text-center mb-2">Create Credentials</h2>
            <p className="text-xs text-slate-500 text-center mb-6">Enter your email and setup password</p>

            <div className="flex flex-col">
              <label className="text-sm font-semibold text-slate-700" htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                placeholder="example@gmail.com"
                value={emailAddress}
                onChange={(e) => {
                  setEmailAddress(e.target.value);
                  setBusinessEmail(e.target.value);
                }}
                className="h-[50px] bg-form-background p-3 mt-1.5 rounded-md text-navy-blue border border-slate-100 focus:border-primary focus:outline-none"
                required
              />
            </div>

            <div className="flex flex-col relative">
              <label className="text-sm font-semibold text-slate-700" htmlFor="password">Password</label>
              <button
                type="button"
                onClick={handleShowNew}
                className="absolute right-4 top-[42px] text-slate-400 hover:text-slate-600 focus:outline-none"
              >
                {showNew ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
              </button>
              <input
                type={inputPassNew}
                id="password"
                placeholder="••••••••"
                value={password}
                onChange={handlePasswordChange}
                className="h-[50px] bg-form-background p-3 mt-1.5 rounded-md text-navy-blue border border-slate-100 focus:border-primary focus:outline-none"
                required
              />
              {passwordError && (
                <span className="text-red-500 text-[10px] mt-1">{passwordError}</span>
              )}
            </div>

            <div className="flex flex-col relative">
              <label className="text-sm font-semibold text-slate-700" htmlFor="confirmPassword">Confirm Password</label>
              <button
                type="button"
                onClick={handleShowConfirm}
                className="absolute right-4 top-[42px] text-slate-400 hover:text-slate-600 focus:outline-none"
              >
                {showConfirm ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
              </button>
              <input
                type={inputPassConfirm}
                id="confirmPassword"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="h-[50px] bg-form-background p-3 mt-1.5 rounded-md text-navy-blue border border-slate-100 focus:border-primary focus:outline-none"
                required
              />
            </div>

            <button
              className="w-full h-[50px] bg-primary text-white text-sm font-bold rounded-md flex items-center justify-center shadow hover:opacity-90 disabled:opacity-50 transition-opacity"
              type="submit"
              disabled={loading || passwordError || !emailAddress || !password || !confirmPassword}
            >
              {loading ? <Spinner className="w-5 h-5 animate-spin border-b-2" /> : "Verify Email"}
            </button>
          </form>
        )}

        {/* STEP 2: Email Verification Sent */}
        {currentStep === 2 && (
          <div className="text-center space-y-6">
            <div className="flex justify-center text-primary">
              <FaEnvelope size={48} className="animate-bounce" />
            </div>
            <h2 className="text-xl font-bold text-navy-blue">Verify your email address</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              We've sent a verification link to <strong className="text-navy-blue">{emailAddress}</strong>. Please click the link to confirm your email, then return here to complete registration.
            </p>

            <div className="pt-4 space-y-3">
              <button
                onClick={() => setCurrentStep(3)}
                className="w-full h-[50px] bg-primary text-white text-sm font-bold rounded-md shadow hover:opacity-90 transition-opacity"
              >
                I have verified my email
              </button>
              <button
                onClick={handleResendVerification}
                disabled={loading}
                className="w-full text-xs font-semibold text-slate-500 hover:text-primary transition-colors focus:outline-none disabled:opacity-50"
              >
                {loading ? "Resending..." : "Resend verification link"}
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Business & Personal Details */}
        {currentStep === 3 && (
          <form onSubmit={handleNextToBank} className="space-y-4">
            <h2 className="text-xl font-bold text-navy-blue text-center mb-1">Tell us about your business</h2>
            <p className="text-xs text-slate-500 text-center mb-4">Complete your profile details below</p>

            <div className="max-h-[360px] overflow-y-auto pr-1 space-y-4 custom-scrollbar">
              <div className="space-y-1">
                <p className="text-xs font-bold text-primary uppercase tracking-wider mb-2 border-b pb-1">Business Profile</p>
                <div className="flex flex-col">
                  <label className="text-xs font-semibold text-slate-700" htmlFor="businessName">Business Name</label>
                  <input
                    type="text"
                    id="businessName"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    placeholder="Omoke Enterprises"
                    className="h-[45px] bg-form-background p-2.5 mt-1 rounded-md text-navy-blue text-sm border border-slate-100 focus:outline-none focus:border-primary"
                    required
                  />
                </div>
                <div className="flex flex-col mt-2">
                  <label className="text-xs font-semibold text-slate-700" htmlFor="businessEmail">Business Email</label>
                  <input
                    type="email"
                    id="businessEmail"
                    value={businessEmail}
                    onChange={(e) => setBusinessEmail(e.target.value)}
                    placeholder="biz@example.com"
                    className="h-[45px] bg-form-background p-2.5 mt-1 rounded-md text-navy-blue text-sm border border-slate-100 focus:outline-none focus:border-primary"
                    required
                  />
                </div>
                <div className="flex flex-col mt-2">
                  <label className="text-xs font-semibold text-slate-700" htmlFor="businessType">Business Type</label>
                  <select
                    id="businessType"
                    value={businessType}
                    onChange={(e) => setBusinessType(e.target.value)}
                    className="h-[45px] bg-form-background px-2.5 mt-1 rounded-md text-navy-blue text-sm border border-slate-100 focus:outline-none focus:border-primary"
                    required
                  >
                    <option value="">Select Business Type</option>
                    {businessTypes.map((biz) => (
                      <option key={biz.id} value={biz.code}>
                        {biz.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col mt-2">
                  <label className="text-xs font-semibold text-slate-700" htmlFor="cac">CAC Number</label>
                  <input
                    type="text"
                    id="cac"
                    value={cac}
                    onChange={(e) => setCac(e.target.value)}
                    placeholder="RC12345678"
                    className="h-[45px] bg-form-background p-2.5 mt-1 rounded-md text-navy-blue text-sm border border-slate-100 focus:outline-none focus:border-primary"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1 pt-2">
                <p className="text-xs font-bold text-primary uppercase tracking-wider mb-2 border-b pb-1">Personal Details</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col">
                    <label className="text-xs font-semibold text-slate-700" htmlFor="firstName">First Name</label>
                    <input
                      type="text"
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="John"
                      className="h-[45px] bg-form-background p-2.5 mt-1 rounded-md text-navy-blue text-sm border border-slate-100 focus:outline-none"
                      required
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-xs font-semibold text-slate-700" htmlFor="lastName">Last Name</label>
                    <input
                      type="text"
                      id="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Doe"
                      className="h-[45px] bg-form-background p-2.5 mt-1 rounded-md text-navy-blue text-sm border border-slate-100 focus:outline-none"
                      required
                    />
                  </div>
                </div>
                <div className="flex flex-col mt-2">
                  <label className="text-xs font-semibold text-slate-700" htmlFor="phone-no">Phone Number</label>
                  <input
                    type="text"
                    id="phone-no"
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                    placeholder="08033045559"
                    className="h-[45px] bg-form-background p-2.5 mt-1 rounded-md text-navy-blue text-sm border border-slate-100 focus:outline-none"
                    required
                  />
                </div>
                <div className="flex flex-col mt-2">
                  <label className="text-xs font-semibold text-slate-700" htmlFor="address">Address</label>
                  <input
                    type="text"
                    id="address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="34, Tokunboh Street, Lagos Island"
                    className="h-[45px] bg-form-background p-2.5 mt-1 rounded-md text-navy-blue text-sm border border-slate-100 focus:outline-none"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div className="flex flex-col">
                    <label className="text-xs font-semibold text-slate-700" htmlFor="state">State</label>
                    <select
                      id="state"
                      value={selectedState}
                      onChange={(e) => setSelectedState(e.target.value)}
                      className="h-[45px] bg-form-background px-2 mt-1 rounded-md text-navy-blue text-sm border border-slate-100 focus:outline-none"
                      required
                    >
                      <option value="">Select State</option>
                      {Object.keys(statesData.states).map((st) => (
                        <option key={st} value={st}>
                          {st}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col">
                    <label className="text-xs font-semibold text-slate-700" htmlFor="city">LGA</label>
                    <select
                      id="city"
                      value={selectedCity}
                      onChange={(e) => setSelectedCity(e.target.value)}
                      className="h-[45px] bg-form-background px-2 mt-1 rounded-md text-navy-blue text-sm border border-slate-100 focus:outline-none"
                      required
                    >
                      <option value="">Select LGA</option>
                      {cities.map((ct) => (
                        <option key={ct} value={ct}>
                          {ct}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <button
              className="w-full h-[50px] bg-primary text-white text-sm font-bold rounded-md shadow hover:opacity-90 transition-opacity"
              type="submit"
            >
              Continue to Bank Details
            </button>
          </form>
        )}

        {/* STEP 4: Bank Details & Verification */}
        {currentStep === 4 && (
          <form onSubmit={handleNextToTerms} className="space-y-5">
            <h2 className="text-xl font-bold text-navy-blue text-center mb-1">Verify Settlement Account</h2>
            <p className="text-xs text-slate-500 text-center mb-4">Validate your business account details</p>

            <div className="flex flex-col">
              <label className="text-sm font-semibold text-slate-700" htmlFor="bank">Bank</label>
              <select
                id="bank"
                value={bank}
                onChange={(e) => setBank(e.target.value)}
                className="h-[50px] bg-form-background px-3 mt-1.5 rounded-md text-navy-blue border border-slate-100 focus:outline-none focus:border-primary"
                required
              >
                <option value="">Select Bank</option>
                {banks.map((b) => (
                  <option key={b.code} value={b.code}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-semibold text-slate-700" htmlFor="account-num">Account Number</label>
              <input
                type="text"
                id="account-num"
                placeholder="0123456789"
                value={accountNumber}
                onChange={handleAccountNumberChange}
                className="h-[50px] bg-form-background p-3 mt-1.5 rounded-md text-navy-blue border border-slate-100 focus:outline-none focus:border-primary"
                required
              />
            </div>

            {/* Verification Loader / Status Display (commented out for now)
            <div className="flex flex-col bg-slate-50 p-4 rounded-lg space-y-1 border border-slate-100">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Account Name</label>
              {verifyingAccount ? (
                <div className="flex items-center gap-2 text-slate-500 text-xs py-1">
                  <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                  <span>Verifying account details...</span>
                </div>
              ) : accountName ? (
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-bold ${accountName === "Account name not found" ? "text-red-500" : "text-navy-blue"}`}>
                    {accountName}
                  </span>
                  {accountName !== "Account name not found" && (
                    <FaCheckCircle className="text-green-500" size={16} />
                  )}
                  {accountName === "Account name not found" && (
                    <FaExclamationCircle className="text-red-500" size={16} />
                  )}
                </div>
              ) : (
                <span className="text-xs text-slate-400 italic">Enter a 10-digit account number to trigger verification</span>
              )}
            </div>
            */}

            <div className="flex flex-col">
              <label className="text-sm font-semibold text-slate-700" htmlFor="account-name">Account Name</label>
              <input
                type="text"
                id="account-name"
                placeholder="Enter account name"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                className="h-[50px] bg-form-background p-3 mt-1.5 rounded-md text-navy-blue border border-slate-100 focus:outline-none focus:border-primary"
                required
              />
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setCurrentStep(3)}
                className="w-1/3 h-[50px] border border-slate-200 text-slate-600 text-sm font-bold rounded-md hover:bg-slate-50 transition-colors"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={!accountName}
                className="flex-1 h-[50px] bg-primary text-white text-sm font-bold rounded-md shadow hover:opacity-90 disabled:opacity-50 transition-opacity"
              >
                Continue
              </button>
            </div>
          </form>
        )}

        {/* STEP 5: Terms & Conditions */}
        {currentStep === 5 && (
          <form onSubmit={handleFinalSubmit} className="space-y-6">
            <h2 className="text-xl font-bold text-navy-blue text-center">Terms of Service</h2>

            <div className="h-[200px] overflow-y-auto bg-slate-50 p-4 rounded-lg text-xs text-slate-500 leading-relaxed custom-scrollbar border border-slate-100">
              <p className="font-bold text-slate-700 mb-2">1. Terms of Use</p>
              <p className="mb-4">
                By signing up for Payloft, you agree to our Terms of Acceptable Use and Data Processing Agreement. We process user identity, financial verification data, and contact credentials strictly in accordance with local banking guidelines and privacy mandates.
              </p>
              <p className="font-bold text-slate-700 mb-2">2. Accuracy of Information</p>
              <p className="mb-4">
                You certify that all registered business structures, registration details, bank accounts, and personal contacts provided are correct, active, and fully authorized.
              </p>
            </div>

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="terms"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="w-4 h-4 mt-0.5 border border-slate-300 rounded text-primary focus:ring-primary focus:outline-none cursor-pointer"
                required
              />
              <label htmlFor="terms" className="text-xs text-navy-blue leading-normal">
                By ticking this box, you agree to Payloft’s{" "}
                <span className="underline text-text-orange font-semibold cursor-pointer">
                  terms of acceptable use and Data Processing Agreement.
                </span>
              </label>
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setCurrentStep(3)}
                className="w-1/3 h-[50px] border border-slate-200 text-slate-600 text-sm font-bold rounded-md hover:bg-slate-50 transition-colors"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading || !agreed}
                className="flex-1 h-[50px] bg-primary text-white text-sm font-bold rounded-md shadow hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center justify-center"
              >
                {loading ? <Spinner className="w-5 h-5 animate-spin border-b-2" /> : "Complete Registration"}
              </button>
            </div>
          </form>
        )}

        {/* STEP 6: Success Page */}
        {currentStep === 6 && (
          <div className="text-center space-y-6 py-6">
            <div className="flex justify-center text-green-500">
              <FaCheckCircle size={64} className="animate-pulse" />
            </div>
            <h2 className="text-2xl font-bold text-navy-blue">Registration Successful!</h2>
            <p className="text-sm text-slate-600 leading-relaxed px-4">
              Your merchant profile has been created successfully. A confirmation email has been sent. You can now log into your dashboard.
            </p>
            <div className="pt-4">
              <Link
                to="/"
                className="block w-full h-[50px] bg-primary text-white text-sm font-bold rounded-md flex items-center justify-center shadow hover:opacity-90 transition-opacity"
              >
                Back to Login
              </Link>
            </div>
          </div>
        )}

        {/* Login Link Footer */}
        {currentStep !== 6 && (
          <p className="mt-6 text-center text-sm text-slate-500">
            Already have an account?{" "}
            <Link className="text-primary font-bold hover:underline" to="/">
              Log in
            </Link>
          </p>
        )}
      </div>
    </div>
  );
};

export default SignUp;
